import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { dynamicAPICall } from "../../../utils/helperFns";
import customFunctions from "../CustomFunctions/customFunctions";
import ErrorMessageHandle from "../Error/ErrorMessageHandle";

interface DropdownProps {
  options?: any[];
  state: any;
  setState: any;
  error?: any;
  register?: any;
  field: any;
  clearErrors?: any;
  callbackEvents?: any;
  control: any;
}

export const Dropdown = ({
  options,
  state,
  setState,
  error,
  field,
  clearErrors,
  callbackEvents,
  control,
}: DropdownProps) => {
  const [optionsList, setOptionsList] = useState(options || []);
  const [defaultValue, setDefaultValue] = useState("");

  useEffect(() => {
    // Extract onload event and query
    const onloadGraphqlEvent = field?.events?.find(
      (x) => x.event === "onload" && x.type === "graphql",
    );
    const onLoadCustomFunction = field?.events?.find(
      (x) => x.event === "onload" && x.type === "customfunction",
    );
    if (onloadGraphqlEvent) {
      const { query = "", responseVariable = "" } = onloadGraphqlEvent?.query || {};
      if (query && responseVariable) {
        dynamicAPICall(query, responseVariable).then((result) => {
          setOptionsList(result || []);
        });
      }
    } else if (onLoadCustomFunction) {
      const values = customFunctions[onLoadCustomFunction?.function_name]();
      setOptionsList(values || []);
    }
  }, [field?.events]);

  useEffect(() => {
    if (optionsList?.length > 0) {
      const stateVal = state?.[field.name];
      let defValue =
        optionsList.find(
          (x) =>
            (x?.value || x?.label) === stateVal?.[0]?.value ||
            (x?.value || x?.label) === stateVal?.[0]?.label ||
            (x?.value || x?.label) === stateVal,
        ) || null;
      if (defValue) {
        setDefaultValue(defValue?.value || defValue?.label);
      } else {
        defValue =
          optionsList.find(
            (x) =>
              (x?.value || x?.label) === field?.default_value?.[0] ||
              (x?.value || x?.label) === field?.default_value,
          ) || null;
        if (defValue) {
          setDefaultValue(defValue?.value || defValue?.label);
        }
      }
    }
  }, [state, field.name, optionsList, field?.default_value]);

  const handleChange = (event: SelectChangeEvent, onChange: any) => {
    const newValue = event.target.value;

    // Clear errors
    if (clearErrors) {
      clearErrors(field?.name);
    }

    // Update state
    setState({ ...state, [field?.name]: newValue });

    // Call React Hook Form's onChange
    onChange(newValue);

    // Handle custom events
    if (field?.events && callbackEvents) {
      callbackEvents(event, field?.events);
    }
  };

  const validateDropdown = (value: any) => {
    const required = field?.validations?.find((x) => x.type === "required");
    if (required && (!value || value === "")) {
      return required?.message || "This field is required";
    }
    return true;
  };

  return (
    <Box id={`${field?.name}_id`}>
      <FormControl fullWidth error={!!error}>
        <Controller
          name={field?.name}
          control={control}
          defaultValue={defaultValue}
          rules={{ validate: validateDropdown }}
          render={({ field: controllerField }) => (
            <Select
              {...controllerField}
              labelId={`${field?.name}-label`}
              id={`${field?.name}-select`}
              value={controllerField.value || defaultValue}
              variant='filled'
              error={!!error}
              onChange={(event) => {
                handleChange(event, controllerField.onChange);
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || selected === "") {
                  return (
                    <span style={{ color: "#999" }}>
                      {field?.placeholder || "Select an option"}
                    </span>
                  );
                }
                const matchedOption = optionsList?.find((x) => (x?.value || x?.label) === selected);
                return matchedOption?.label || selected;
              }}>
              {optionsList?.map((item, index) => {
                return (
                  <MenuItem
                    key={index}
                    value={item?.value || item?.label}
                    disabled={item?.disabled ? item?.disabled || false : false}>
                    {item?.label}
                  </MenuItem>
                );
              })}
            </Select>
          )}
        />
      </FormControl>
      <ErrorMessageHandle error={error} />
    </Box>
  );
};

Dropdown.defaultProps = {
  options: [],
  error: undefined,
  register: () => {},
  clearErrors: () => {},
  callbackEvents: () => {},
};
